import {
  periodTitle,
  type Attendance,
  type OccurrenceKey,
  type ScheduleEntry,
  type ScheduleEntryMonth,
} from "@edu/shared";
import type { ApiErrorCode } from "../../lib/api.ts";

/**
 * Ключи и слова полосы отметок в карточке занятия.
 *
 * Отдельным файлом от разметки (`EntryMarks.tsx`) по той же причине, что у слов
 * группового расписания (`group-text.ts`): здесь собираются фразы, которые
 * человек читает, и выводится ключ, по которому уезжает отметка, а и то и другое
 * ломается молча. Неверный ключ пишет отметку не тому занятию, неверная подпись
 * обещает не то действие, и увидеть это можно только тестом.
 *
 * ⚠️ Импорт из `lib/api.ts` здесь ТОЛЬКО типом (`import type`), и это условие
 * работы тестов: значением он утащил бы в прогон весь клиент API вместе с его
 * браузерными зависимостями. Коды отказов при этом остаются типизированными,
 * то есть ветка мимо словаря не собирается.
 */

/**
 * Ключ занятия из строки сетки.
 *
 * ⚠️ Правило то же, что в `occurrenceKeyOfLesson`, и повторено оно здесь потому,
 * что у строки сетки исходные поля лежат плоско, а урока у неё может не быть
 * вовсе. Занятие серии адресуется парой ВСЕГДА, даже когда урок уже создан.
 */
export function occurrenceKeyOfEntry(entry: ScheduleEntry): OccurrenceKey | null {
  if (entry.slotId !== null && entry.occursOn !== null) {
    return { kind: "occurrence", slotId: entry.slotId, occursOn: entry.occursOn };
  }
  return entry.lessonId === null ? null : { kind: "lesson", lessonId: entry.lessonId };
}

/**
 * Что показывает полоса отметок, либо `null`, если её нет вовсе.
 *
 * Отдельной функцией, потому что это правило ВИДИМОСТИ, а видимость здесь
 * означает приватность: полоса это записи преподавателя о паре, и лишний чип на
 * чужом экране это не косметика. Проверить такое можно только тестом, потому
 * что все три ветки молчаливые: ошибка в них не падает, она просто рисует или
 * не рисует.
 *
 * ⚠️ **Единиц две, и живут они порознь.** Чипы посещения и поурочной оплаты
 * держатся на КЛЮЧЕ ЗАНЯТИЯ, а чип месяца на паре «ученик и период»: занятий в
 * месяце может не быть ни одного, платят вперёд. Раньше пустой ключ гасил всю
 * полосу целиком, и с приездом месяцев это стало бы молчанием там, где
 * отмечать есть что.
 *
 * ⚠️ Полосы нет, когда сервер полей не прислал: «не отмечено» и «этот вопрос
 * здесь не задают» это разные ответы, и различаются они отсутствием ключа, а не
 * значением `false`.
 *
 * ⚠️ Нет её и у отменённого или уехавшего занятия: платят за состоявшееся.
 * Ученику полос не бывает по построению (сервер этих полей ему не отдаёт), и
 * второй проверки роли здесь нет намеренно, иначе правило разъехалось бы на два
 * места.
 */
export interface EntryMarksView {
  /** Кому пишется отметка. */
  studentUserId: string;
  /**
   * Чипы, у которых единица это ЗАНЯТИЕ, и адрес отметки для них. `null`, когда
   * занятие нечем адресовать либо когда таких чипов не спрашивают вовсе.
   *
   * ⚠️ Оба флага отвечают на вопрос «показывать ли чип», а не «отмечено ли»:
   * само значение отметки лежит в строке сетки и читается из неё разметкой.
   */
  occurrence: { key: OccurrenceKey; showAttendance: boolean; showPaid: boolean } | null;
  /** Чип, у которого единица это МЕСЯЦ. */
  month: ScheduleEntryMonth | null;
}

export function marksView(entry: ScheduleEntry): EntryMarksView | null {
  if (entry.state === "cancelled" || entry.state === "moved_away") return null;

  const studentUserId = entry.counterpartUserId;
  if (studentUserId === null) return null;

  const key = occurrenceKeyOfEntry(entry);
  const showAttendance = entry.attendance !== undefined;
  const showPaid = entry.paid !== undefined;
  const occurrence =
    key !== null && (showAttendance || showPaid) ? { key, showAttendance, showPaid } : null;
  const month = entry.month ?? null;

  return occurrence === null && month === null ? null : { studentUserId, occurrence, month };
}

/** Следующее состояние посещения по кругу: сначала частый случай, потом редкий. */
export function nextAttendance(current: Attendance | null): Attendance | null {
  if (current === null) return "present";
  if (current === "present") return "absent";
  return null;
}

/**
 * Подпись чипа месяца и подсказка к нему.
 *
 * ⚠️ **Единица здесь МЕСЯЦ, а не занятие, и сказано это в САМОЙ ПОДПИСИ.**
 * Соседний чип поурочной оплаты говорит «отметить оплату», и та же фраза у
 * месяца означала бы, что нажатие отмечает занятие под ней. Различают их два
 * слова, имя месяца и «весь», и оба стоят в подписи. В подсказке им не место:
 * на телефоне подсказки нет вовсе, а разница между «за это занятие» и «за весь
 * месяц» это разница в деньгах, и открывать её наведением мыши нельзя. Правка
 * 03.09.2026, до неё «весь» стояло только в подсказке.
 *
 * ⚠️ Подсказка поэтому либо говорит СВОЁ, либо не приезжает вовсе. Своё у неё
 * ровно одно: у отмеченного месяца надо сказать, что нажатие обратимо
 * (подтверждения у действия нет). У неотмеченного добавить нечего, и `null`
 * здесь честнее, чем подпись, пересказанная другими словами.
 *
 * ⚠️ Слова «просрочен» здесь нет и не будет: срока мы не знаем, какого числа
 * семья договорилась переводить деньги, продукту неизвестно. Не отмеченный месяц
 * это отсутствие отметки, а не долг, и тревожного цвета у него тоже нет
 * (решение владельца 21.08.2026).
 */
export function monthChipText(
  period: string,
  paid: boolean,
): { label: string; title: string | null } {
  const month = periodTitle(period);
  return paid
    ? { label: `весь ${month} оплачен`, title: "нажмите, чтобы снять отметку" }
    : { label: `отметить оплату за весь ${month}`, title: null };
}

/**
 * Отказ ручки месяцев словами, либо `null`, если случай не наш.
 *
 * ⚠️ **Красной строки у этих двух отказов нет намеренно.** Оба означают, что
 * сетка на экране устарела, а не что человек сделал что-то не то: режим оплаты
 * могли сменить в соседней вкладке между отрисовкой недели и нажатием. Красное
 * читалось бы как счёт в адрес преподавателя, а его правило запрещает
 * (решение владельца 23.08.2026, раздел 5.10 CLAUDE.md). Строка говорит ровно
 * два факта: отметки не случилось и почему.
 *
 * ⚠️ `null` означает «отказ не про это» и возвращает поведение по умолчанию:
 * такой отказ уходит наверх и печатается общей красной строкой карточки. Молча
 * глотать всё подряд нельзя, иначе сетевой обрыв выглядел бы как успешная
 * отметка.
 *
 * `invalid_body` это период вне окна самой ручки. После того как сервер стал
 * присылать месяц только внутри окна чипов, он недостижим, но ветка написана:
 * недостижимое сегодня становится достижимым от одной правки окна.
 */
export function monthMarkNote(code: ApiErrorCode): string | null {
  if (code === "payment_not_tracked") {
    return "Месяц не отмечен: у этого ученика уже не помесячная оплата.";
  }
  if (code === "invalid_body") {
    return "Месяц не отмечен: отмечать можно только ближайшие месяцы.";
  }
  return null;
}
